import { formatDisplayFullDate } from '@/config';
import { formatCurrency } from '@/helpers/format';
import { apiSdk } from '@/httpClient';
import { TicketModificationHistoryModel, TicketModificationKind } from '@/__generated';
import { Modal, Table } from 'antd';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  ticketId: string;
  ticketRefNo?: string;
  onClose: () => void;
}

const KIND_KEYS: Record<TicketModificationKind, string> = {
  1: 'ticketModifications_KindDeparture',
  2: 'ticketModifications_KindSeat',
  4: 'ticketModifications_KindTariff',
  8: 'ticketModifications_KindName',
  16: 'ticketModifications_KindTransfer',
  32: 'ticketModifications_KindHolder',
  64: 'ticketModifications_KindDestination',
};

/** Modifications d'un billet (chaîne des billets remplacés) : quoi, montants, qui (« Modifié par »), caisse, approbation. */
export default function TicketModificationsModal({ ticketId, ticketRefNo, onClose }: Props): ReactElement {
  const [t] = useTranslation();
  const [rows, setRows] = React.useState<TicketModificationHistoryModel[]>();
  React.useEffect(() => {
    apiSdk.TicketModificationPolicyService.history({ ticketId })
      .then(setRows)
      .catch(() => setRows([]));
  }, [ticketId]);
  return (
    <Modal visible={true} width={1000} title={`${t('ticketModifications_Title')} ${ticketRefNo || ''}`} footer={null} onCancel={onClose}>
      <Table<TicketModificationHistoryModel>
        rowKey="id"
        size="small"
        loading={rows === undefined}
        dataSource={rows || []}
        pagination={false}
        scroll={{ x: 900 }}
        locale={{ emptyText: t('ticketModifications_None') }}
        columns={[
          { title: t('ticketModifications_Date'), dataIndex: 'createdOn', width: 140, render: v => (v ? formatDisplayFullDate(v) : '') },
          {
            title: t('ticketModifications_Tickets'),
            width: 170,
            render: (_, r) => `${r.oldTicketRefNo || ''} → ${r.newTicketRefNo || ''}`,
          },
          {
            title: t('ticketModifications_Changes'),
            dataIndex: 'kinds',
            render: (kinds?: TicketModificationKind[]) => (kinds || []).map(k => t(KIND_KEYS[k] || String(k))).join(', '),
          },
          { title: t('ticketModifications_Difference'), dataIndex: 'difference', width: 110, render: v => formatCurrency(v) },
          {
            title: t('ticketModifications_Fee'),
            width: 110,
            render: (_, r) => (r.feeWaived ? t('ticketModifications_FeeWaived') : formatCurrency(r.fee)),
          },
          { title: t('ticketModifications_CreditNote'), dataIndex: 'creditNoteNumber', width: 120, render: v => v || '—' },
          { title: t('ticketModifications_ModifiedBy'), dataIndex: 'modifiedBy', width: 150 },
          { title: t('ticketModifications_Register'), dataIndex: 'registerName', width: 110, render: v => v || '—' },
          { title: t('ticketModifications_ApprovedBy'), dataIndex: 'approvedBy', width: 130, render: v => v || '—' },
        ]}
      />
    </Modal>
  );
}
